import type { User } from 'firebase/auth';
import { GoogleAuthProvider, onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth';
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError, apiFetch } from '../api/client';
import { firebaseAuth } from '../firebase';

import { AuthContext } from './AuthContext';
import type { AuthContextValue, AuthState } from './AuthContext';

// 認可判定は backend の /api/auth/check に一本化 (ALLOWED_EMAIL_DOMAIN / ALLOWED_EMAILS を
// worker-api の wrangler.toml だけで管理するため)。FE では domain / allowlist を持たない。
const checkAuthorization = async (): Promise<{ email: string }> =>
  await apiFetch<{ user: { email: string } }>('/api/auth/check').then((r) => ({
    email: r.user.email,
  }));

const displayEmail = (user: User): string =>
  user.email ?? user.providerData.find((p) => p.email !== null)?.email ?? '';

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  useEffect(() => {
    return onAuthStateChanged(firebaseAuth, (user) => {
      if (user === null) {
        setState({ status: 'signed-out' });
        return;
      }
      setState({ status: 'loading' });
      checkAuthorization()
        .then(({ email }) => {
          setState({ status: 'signed-in', user, email });
        })
        .catch((e: unknown) => {
          // 403 (Email domain not allowed) は forbidden UI へ。それ以外の失敗は再ログイン誘導。
          const isForbidden = e instanceof ApiError && e.status === 403;
          void signOut(firebaseAuth).then(() => {
            setState(
              isForbidden
                ? { status: 'forbidden', email: displayEmail(user) }
                : { status: 'signed-out' },
            );
          });
        });
    });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      signInWithGoogle: async () => {
        const provider = new GoogleAuthProvider();
        await signInWithPopup(firebaseAuth, provider);
      },
      signOutCurrentUser: async () => {
        await signOut(firebaseAuth);
      },
    }),
    [state],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
};
